import { useState, useCallback, useEffect } from 'react';
import { Box } from 'theme-ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar, A11y } from 'swiper';

import store, { useRecoilValue } from '@store';
import { useSelectedOptions } from '@hooks';
import { ShopifyMedia, Badge } from '@snippets';

import { Thumbs } from './Thumbs';
import { themed } from './Media.theme';

export const Media = themed(
  ({ theme, product, selectedVariant, aboveTheFold, preload }) => {
    const selectedMedia = useRecoilValue(store.selectedMedia$);
    const [selectedOptions] = useSelectedOptions();
    const [activeIndex, setActiveIndex] = useState(0);
    const [swiper, setSwiper] = useState();
    const goToSlide = useCallback(
      (index) => {
        swiper.slideTo(index);
        setActiveIndex(index);
      },
      [swiper]
    );

    useEffect(() => {
      if (!swiper || !swiper.initialized) return undefined;
      const handleSlideChange = (_swiper) => {
        setActiveIndex(_swiper.realIndex);
      };
      swiper.on('slideChange', handleSlideChange);
      return () => {
        swiper.off('slideChange', handleSlideChange);
      };
    }, [swiper]);

    // on color swatch change go back to first slide
    useEffect(() => {
      if (!swiper || !swiper.initialized) return;
      if (!product) return;
      if (product.isGrouped) {
        swiper.slideTo(0);
        return;
      }

      const variantImageIndex = product.media?.findIndex(({ image }) => {
        return image?.id === selectedVariant?.image?.id;
      });
      if (variantImageIndex >= 0) {
        swiper.slideTo(variantImageIndex);
      }
    }, [selectedOptions?.Color, product?.id, swiper]);

    return (
      <Box data-comp={Media.displayName} sx={theme.medias}>
        {/* Thumbs */}
        <Thumbs
          medias={selectedMedia}
          activeIndex={activeIndex}
          goToSlide={goToSlide}
        />

        <Box
          sx={{
            position: 'relative',
            flex: 1,
            pb: [10, null, 0],
          }}
        >
          {/* Carousel */}
          <Swiper
            data-comp="MediaSwiper"
            modules={[Navigation, Scrollbar, A11y]}
            pagination={{
              el: '.swiper-pagination',
              type: 'bullets',
              clickable: true,
            }}
            preloadImages
            fadeEffect={{ crossFade: true }}
            onSwiper={setSwiper}
            slidesPerView={1}
            freeMode={false}
            sx={{
              ...theme.swiper,
              cursor: selectedMedia?.length > 1 ? 'grab' : 'auto',
            }}
            watchOverflow
          >
            {/* Ech large media */}
            {selectedMedia?.map((media, index) => {
              const isPrimaryIamge = index === 0 && aboveTheFold;

              return (
                <SwiperSlide key={index} virtualIndex={index}>
                  <ShopifyMedia
                    media={media}
                    bgColor="lightGray"
                    aboveTheFold={isPrimaryIamge}
                    preload={isPrimaryIamge}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>

          {/* Placeholder */}
          {!swiper && (
            <Box
              sx={{
                width: '100%',
                pb: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                bg: 'lightGray',
                zIndex: 1,
              }}
            />
          )}

          <Badge handle={product?.handle} product={product} isPdp />

          {/* Bullets */}
          <div className="swiper-pagination" sx={theme.bullets} />
        </Box>
      </Box>
    );
  }
);

Media.displayName = 'Media';
